/**
 * Plan notices over the map (paywall plan P4, redesign 2026-11): small glass notices at the top of the viewport. They
 * explain, they don't nag: at most two at a time, each can be closed (for this browser session), and nobody who plans
 * alone ever sees one (they need a shared world or a plan of the person's own).
 *
 *   world paused          the owner's plan doesn't cover this shared world (anyone in it)
 *   payment failed        the owner's renewal didn't go through: 7 days of grace, "Update card"
 *   free days end soon    a Checkout trial (Cloud, Team) ends in 2 days or less: then the first payment, "Manage"
 *   trial ends soon       2 days or less left of the card-free trial (servers before the Checkout trial)
 *   plan ends soon        a cancelled subscription (or cancelled free days) ends within 3 days, "Keep my subscription"
 */
import { Info, TriangleAlert, X } from "lucide-react";
import { useState } from "react";
import { useApp } from "../state/store.ts";
import { openAccountMenu } from "../lib/auth.ts";
import { cancelSubscription, daysLeft, fmtDate, fmtPrice, openLiveWorlds, openPlans, openPortal, priceIn, useBilling } from "../lib/billing.ts";
import "./plans.css";

const KEY = "mcp-banners-closed";
const closedNow = (): string[] => { try { return JSON.parse(sessionStorage.getItem(KEY) ?? "[]"); } catch { return []; } };

interface Banner { id: string; tone: "warn" | "info"; text: React.ReactNode; actions?: React.ReactNode }

const inDays = (d: number) => (d <= 0 ? "today" : d === 1 ? "tomorrow" : `in ${d} days`);

export function Banners() {
  const room = useApp((s) => s.room);
  const status = useBilling((s) => s.status);
  const prices = useBilling((s) => s.prices);
  const busy = useBilling((s) => s.busy);
  const [closed, setClosed] = useState<string[]>(closedNow);
  const server = useApp((s) => s.collab.url);

  const list: Banner[] = [];
  const plan = status?.plan;
  const sub = status?.subscription;
  const cancelled = !!sub && (sub.cancel_at_period_end || !!sub.cancel_at);

  if (room?.kind === "account" && room.paused) {
    const owner = room.role === "owner";
    const tooMany = owner && !!plan && plan.max_worlds > 0 && (status?.usage.worlds ?? 0) > plan.max_worlds;
    list.push({
      id: `paused:${room.id}`,
      tone: "warn",
      text: owner
        ? tooMany
          ? <>This world is paused: your {plan!.name} plan keeps {plan!.max_worlds} world{plan!.max_worlds === 1 ? "" : "s"} live. Everyone can still look around and export.</>
          : <>This world is paused: your plan ended. Everyone can still look around and export, and nothing is deleted.</>
        : <>This world is paused: {room.ownerName ?? "the owner"}'s plan ended. You can still look around and export.</>,
      actions: owner && (
        <>
          {tooMany && <button type="button" className="btn btn-sm btn-ghost" onClick={openLiveWorlds}>Choose which stay live</button>}
          <button type="button" className="btn btn-sm btn-primary" onClick={() => openPlans("Pick a plan to plan together in this world again.")}>See plans</button>
        </>
      ),
    });
  }
  if (sub?.status === "past_due" && plan?.source === "subscription") {
    list.push({
      id: `past_due:${sub.id}`,
      tone: "warn",
      text: <>Your last payment for {plan.name} didn't go through. Update your card by {fmtDate(plan.expires_at)} to keep planning together.</>,
      actions: <button type="button" className="btn btn-sm btn-primary" disabled={!!busy} onClick={() => void openPortal(server)}>Update card</button>,
    });
  }
  if (sub?.status === "trialing" && sub.trial_end && daysLeft(sub.trial_end) <= 2) {
    const d = daysLeft(sub.trial_end);
    const price = prices.find((p) => p.plan === sub.plan);
    const then = price ? (() => { const x = priceIn(price, sub.currency); return `${fmtPrice(x.amount, x.currency)}${price.interval === "month" ? "\u00a0/\u00a0month" : ""}`; })() : "";
    const name = plan?.name ?? (sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1));
    list.push(cancelled
      ? {
        id: `trial-cancelled:${sub.id}`,
        tone: "info",
        text: <>Your {name} trial ends {inDays(d)}. You cancelled it, so you won't be charged; your shared worlds pause then.</>,
        actions: <button type="button" className="btn btn-sm btn-ghost" disabled={!!busy} onClick={() => void cancelSubscription(server, true)}>Keep {name}</button>,
      }
      : {
        id: `trialing:${sub.id}`,
        tone: "info",
        text: <>Your {name} trial ends {inDays(d)}{then ? <> · then <span className="tabular">{then}</span></> : null}</>,
        actions: <button type="button" className="btn btn-sm btn-ghost" onClick={() => openAccountMenu()}>Manage</button>,
      });
  }
  if (plan?.source === "trial" && plan.expires_at && daysLeft(plan.expires_at) <= 2) {
    const d = daysLeft(plan.expires_at);
    list.push({
      id: "trial-ending",
      tone: "info",
      text: <>Your free trial ends {inDays(d)}. After that your shared world pauses (nothing is deleted).</>,
      actions: <button type="button" className="btn btn-sm btn-primary" onClick={() => openPlans()}>See plans</button>,
    });
  }
  if (plan?.source === "subscription" && sub && sub.status !== "past_due" && sub.status !== "trialing" && cancelled && plan.expires_at && daysLeft(plan.expires_at) <= 3) {
    list.push({
      id: `ending:${sub.id}`,
      tone: "info",
      text: <>Your {plan.name} plan ends on {fmtDate(plan.expires_at)} (you cancelled it). Your shared worlds pause then.</>,
      actions: <button type="button" className="btn btn-sm btn-ghost" disabled={!!busy} onClick={() => void cancelSubscription(server, true)}>Keep my subscription</button>,
    });
  }

  const shown = list.filter((b) => !closed.includes(b.id)).slice(0, 2);
  if (!shown.length) return null;
  const close = (id: string) => {
    const next = [...closed, id];
    setClosed(next);
    try { sessionStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };
  return (
    <div className="banners mcp-acct" role="status">
      {shown.map((b) => (
        <div key={b.id} className={`banner mcp-banner glass ${b.tone}`} data-banner={b.id.split(":")[0]}>
          <span className="banner-ico" aria-hidden="true">{b.tone === "warn" ? <TriangleAlert size={15} /> : <Info size={15} />}</span>
          <span className="banner-text">{b.text}</span>
          {b.actions && <span className="banner-actions">{b.actions}</span>}
          <button type="button" className="btn btn-sm btn-ghost btn-icon banner-close" title="Hide for now" aria-label="Hide for now" onClick={() => close(b.id)}><X size={14} aria-hidden="true" /></button>
        </div>
      ))}
    </div>
  );
}
